/** Time helpers — storage is 24h HH:mm; UI uses 12-hour AM/PM. */

/** Convert any common time string to 24-hour HH:mm for storage/scheduling. */
export function normalizeTime(value: string): string {
  const raw = value.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
  if (!raw) return value.trim();

  // 12-hour: 3pm, 3:00pm, 3:00 pm, 12:15 am
  const twelve = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (twelve) {
    let hours = Number(twelve[1]);
    const minutes = Number(twelve[2] ?? '0');
    const period = twelve[3];
    if (hours < 1 || hours > 12 || minutes > 59) return value.trim();
    if (period === 'am') {
      hours = hours === 12 ? 0 : hours;
    } else {
      hours = hours === 12 ? 12 : hours + 12;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  // 24-hour HH:mm or H:mm
  const twentyFour = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFour) {
    const hours = Number(twentyFour[1]);
    const minutes = Number(twentyFour[2]);
    if (hours > 23 || minutes > 59) return value.trim();
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  return value.trim();
}

/** Format stored HH:mm (or flexible input) as 12-hour time, e.g. "3:00 PM". */
export function formatTime12(value: string): string {
  if (!value) return '';
  const normalized = normalizeTime(value);
  const match = normalized.match(/^(\d{2}):(\d{2})$/);
  if (!match) return value;
  let hours = Number(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${period}`;
}

export function formatTimeRange12(start: string, end: string): string {
  return `${formatTime12(start)} – ${formatTime12(end)}`;
}

export function timeToMinutes(time: string): number {
  const normalized = normalizeTime(time);
  const [h, m] = normalized.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTime(total: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, total));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function addMinutesToTime(time: string, minutes: number): string {
  return minutesToTime(timeToMinutes(time) + minutes);
}

export function rangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function eachDateKey(start: Date, end: Date): string[] {
  const keys: string[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);
  while (cursor <= last) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

export function isWeekend(dateKey: string): boolean {
  const day = parseDateKey(dateKey).getDay();
  return day === 0 || day === 6;
}
